import React, { useEffect, useRef, useCallback, useState } from 'react';
import {
  Dialog, Typography, Avatar, Box, IconButton, Slide, keyframes
} from '@mui/material';
import CallIcon from '@mui/icons-material/Call';
import CallEndIcon from '@mui/icons-material/CallEnd';
import ChatIcon from '@mui/icons-material/Chat';
import VideocamIcon from '@mui/icons-material/Videocam';
import api from '../../services/api';

// Pulsing ring animation around avatar
const pulseRing = keyframes`
  0% { transform: scale(1); opacity: 0.6; }
  50% { transform: scale(1.3); opacity: 0; }
  100% { transform: scale(1); opacity: 0; }
`;

const pulseRing2 = keyframes`
  0% { transform: scale(1); opacity: 0.4; }
  50% { transform: scale(1.5); opacity: 0; }
  100% { transform: scale(1); opacity: 0; }
`;

// Gentle bounce on the accept button
const bounceIn = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

// Generate a loud, phone-like ringtone using Web Audio API
function createRingtone() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const gainNode = ctx.createGain();
    gainNode.gain.value = 0.5;
    gainNode.connect(ctx.destination);

    let playing = true;
    const playTone = () => {
      if (!playing) return;
      // Ring pattern: two quick tones, pause, repeat (like a real phone)
      const osc1 = ctx.createOscillator();
      osc1.type = 'sine';
      osc1.frequency.value = 440;
      osc1.connect(gainNode);
      osc1.start();
      osc1.stop(ctx.currentTime + 0.4);

      const osc2 = ctx.createOscillator();
      osc2.type = 'sine';
      osc2.frequency.value = 480;
      osc2.connect(gainNode);
      osc2.start();
      osc2.stop(ctx.currentTime + 0.4);

      // Second ring
      const osc3 = ctx.createOscillator();
      osc3.type = 'sine';
      osc3.frequency.value = 440;
      osc3.connect(gainNode);
      osc3.start(ctx.currentTime + 0.5);
      osc3.stop(ctx.currentTime + 0.9);

      const osc4 = ctx.createOscillator();
      osc4.type = 'sine';
      osc4.frequency.value = 480;
      osc4.connect(gainNode);
      osc4.start(ctx.currentTime + 0.5);
      osc4.stop(ctx.currentTime + 0.9);

      if (playing) {
        setTimeout(playTone, 2000);
      }
    };

    playTone();

    return {
      stop: () => {
        playing = false;
        ctx.close().catch(() => {});
      },
    };
  } catch (e) {
    console.warn('Ringtone not supported:', e);
    return { stop: () => {} };
  }
}

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * IncomingCallModal — Full-screen phone-call-like overlay for astrologers.
 * Designed to be impossible to miss — dark backdrop, pulsing avatar, loud ring.
 * Shows a persistent browser notification even if muted/tab hidden.
 */
const IncomingCallModal = ({
  open,
  call,
  onAccepted,
  onRejected,
  autoRejectAfter = 30000,
}) => {
  const timerRef = useRef(null);
  const ringtoneRef = useRef(null);
  const notifRef = useRef(null);
  const [countdown, setCountdown] = useState(30);
  const countdownRef = useRef(null);

  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.stop();
      ringtoneRef.current = null;
    }
    if (notifRef.current) {
      try { notifRef.current.close(); } catch (e) { /* ok */ }
      notifRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!open || !call) {
      stopRingtone();
      setCountdown(Math.floor(autoRejectAfter / 1000));
      if (countdownRef.current) clearInterval(countdownRef.current);
      return;
    }

    // Start ringtone
    ringtoneRef.current = createRingtone();

    // Countdown timer
    setCountdown(Math.floor(autoRejectAfter / 1000));
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Persistent browser notification — shows even when muted or tab hidden
    if ('Notification' in window && Notification.permission === 'granted') {
      const typeLabel = { chat: 'Chat', call: 'Voice Call', video: 'Video Call' }[call.type] || 'Consultation';
      try {
        notifRef.current = new Notification(`Incoming ${typeLabel}`, {
          body: `${call.userName || 'User'} is calling you`,
          icon: '/logo192.png',
          tag: 'incoming-call',
          requireInteraction: true,
          silent: false,
        });
        notifRef.current.onclick = () => { window.focus(); notifRef.current.close(); };
      } catch (e) { /* Notification not available */ }
    }

    timerRef.current = setTimeout(handleReject, autoRejectAfter);
    return () => {
      clearTimeout(timerRef.current);
      clearInterval(countdownRef.current);
      stopRingtone();
    };
  }, [open, call]);

  const handleAccept = async () => {
    clearTimeout(timerRef.current);
    clearInterval(countdownRef.current);
    stopRingtone();
    try {
      await api.post(`/consultations/${call.consultationId}/accept`);
      onAccepted && onAccepted(call.chatRoomId, call.consultationId, call.type, call.ratePerMin);
    } catch (err) {
      console.error('Accept failed', err);
    }
  };

  const handleReject = async () => {
    clearTimeout(timerRef.current);
    clearInterval(countdownRef.current);
    stopRingtone();
    try {
      await api.post(`/consultations/${call.consultationId}/reject`);
    } catch (err) {
      console.error('Reject failed', err);
    }
    onRejected && onRejected();
  };

  const TypeIcon = call?.type === 'video' ? VideocamIcon
    : call?.type === 'call' ? CallIcon : ChatIcon;

  const typeLabel = { chat: 'Chat Request', call: 'Voice Call', video: 'Video Call' }[call?.type] || 'Consultation';

  return (
    <Dialog
      open={open}
      fullScreen
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          userSelect: 'none',
        }
      }}
    >
      {/* Top section: type label */}
      <Box sx={{ position: 'absolute', top: 40, textAlign: 'center', width: '100%' }}>
        <TypeIcon sx={{ fontSize: 28, color: 'rgba(255,255,255,0.7)', mb: 0.5 }} />
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', letterSpacing: 2, textTransform: 'uppercase', fontSize: 13 }}>
          Incoming {typeLabel}
        </Typography>
      </Box>

      {/* Center: pulsing avatar + caller info */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: -4 }}>
        {/* Pulsing rings */}
        <Box sx={{ position: 'relative', mb: 3 }}>
          <Box sx={{
            position: 'absolute', top: -15, left: -15,
            width: 130, height: 130, borderRadius: '50%',
            border: '2px solid rgba(76, 175, 80, 0.5)',
            animation: `${pulseRing} 2s ease-out infinite`,
          }} />
          <Box sx={{
            position: 'absolute', top: -25, left: -25,
            width: 150, height: 150, borderRadius: '50%',
            border: '2px solid rgba(76, 175, 80, 0.3)',
            animation: `${pulseRing2} 2s ease-out infinite 0.5s`,
          }} />
          <Avatar sx={{
            width: 100, height: 100,
            bgcolor: '#e91e63',
            fontSize: 42,
            fontWeight: 300,
            border: '3px solid rgba(255,255,255,0.3)',
          }}>
            {call?.userName?.[0]?.toUpperCase() || '?'}
          </Avatar>
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 300, mb: 0.5 }}>
          {call?.userName || 'User'}
        </Typography>

        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1 }}>
          &#8377;{call?.ratePerMin}/min
        </Typography>

        <Typography variant="body2" sx={{
          color: countdown <= 10 ? '#ff5252' : 'rgba(255,255,255,0.5)',
          fontVariantNumeric: 'tabular-nums',
          transition: 'color 0.3s',
        }}>
          Auto-decline in {countdown}s
        </Typography>
      </Box>

      {/* Bottom: Accept / Reject buttons */}
      <Box sx={{
        position: 'absolute',
        bottom: 60,
        display: 'flex',
        justifyContent: 'center',
        gap: 10,
        width: '100%',
      }}>
        {/* Reject */}
        <Box sx={{ textAlign: 'center' }}>
          <IconButton
            onClick={handleReject}
            aria-label="Reject call"
            sx={{
              bgcolor: '#f44336',
              color: '#fff',
              width: 64, height: 64,
              '&:hover': { bgcolor: '#d32f2f' },
              boxShadow: '0 4px 20px rgba(244, 67, 54, 0.4)',
            }}
          >
            <CallEndIcon sx={{ fontSize: 32 }} />
          </IconButton>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'rgba(255,255,255,0.7)' }}>
            Decline
          </Typography>
        </Box>

        {/* Accept */}
        <Box sx={{ textAlign: 'center' }}>
          <IconButton
            onClick={handleAccept}
            aria-label="Accept call"
            sx={{
              bgcolor: '#4caf50',
              color: '#fff',
              width: 64, height: 64,
              '&:hover': { bgcolor: '#388e3c' },
              boxShadow: '0 4px 20px rgba(76, 175, 80, 0.4)',
              animation: `${bounceIn} 1.5s ease-in-out infinite`,
            }}
          >
            {call?.type === 'chat'
              ? <ChatIcon sx={{ fontSize: 32 }} />
              : <CallIcon sx={{ fontSize: 32 }} />}
          </IconButton>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'rgba(255,255,255,0.7)' }}>
            Accept
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
};

export default IncomingCallModal;
